import { pointInPolygon } from "./geometry";
import { paintedRegionPolygons, waterRegionPolygons } from "./painted-regions";
import { windStrength } from "./wind";
import type { EnvironmentMaterial, EnvironmentSoundEvent, MaterialRegion, Point, Scene, Token } from "../types";

export const ENVIRONMENT_MATERIAL_LABELS: Record<EnvironmentMaterial, string> = {
  ground: "Ground",
  vegetation: "Vegetation",
  wood: "Wood",
  stone: "Stone",
  metal: "Metal",
  ice: "Ice",
  snow: "Snow cover",
  mud: "Mud",
  lava: "Lava",
};

export const ENVIRONMENT_MATERIALS = Object.keys(ENVIRONMENT_MATERIAL_LABELS) as EnvironmentMaterial[];

const RAIN_LEVEL = { off: 0, light: 0.34, medium: 0.68, heavy: 1 } as const;
const SNOW_LEVEL = { off: 0, light: 0.32, medium: 0.66, heavy: 1 } as const;
const LIGHTNING_LEVEL = { off: 0, distant: 0.3, storm: 0.7, severe: 1 } as const;

export interface EnvironmentLevels {
  rain: number;
  snow: number;
  wind: number;
  lightning: number;
  fire: number;
  water: number;
  light: number;
}

export interface MaterialReaction {
  wetness: number;
  snowCover: number;
  windMotion: number;
  heat: number;
  reflection: number;
  steam: number;
  glow: number;
  audio: number;
}

export function fireStrength(token: Token): number {
  if (token.effect === "campfire" || token.effect === "volumetric-fire") return 0.72;
  if (token.effect !== "volume-fire") return 0;
  return token.volumeFire?.intensity === "high" ? 1 : token.volumeFire?.intensity === "low" ? 0.42 : 0.72;
}

export function environmentLevels(scene: Scene): EnvironmentLevels {
  const fires = scene.tokens.map(fireStrength).filter(Boolean);
  const waters = scene.waterRegions ?? [];
  const enabledLights = scene.geometry?.lights.filter((light) => light.enabled) ?? [];
  const directLight = enabledLights.length ? enabledLights.reduce((sum, light) => sum + Math.max(0, light.intensity), 0) / Math.max(1, enabledLights.length * 1.5) : 0;
  return {
    rain: RAIN_LEVEL[scene.rain ?? "off"],
    snow: SNOW_LEVEL[scene.snow ?? "off"],
    wind: Math.min(1, windStrength(scene.windSpeed) / 2.35),
    lightning: LIGHTNING_LEVEL[scene.lightning ?? "off"],
    fire: fires.length ? Math.min(1, fires.reduce((sum, value) => sum + value, 0) / 2) : 0,
    water: waters.length ? waters.reduce((sum, region) => sum + Math.max(0.2, region.movement ?? 0.55), 0) / waters.length : 0,
    light: Math.min(1, Math.max(scene.geometry?.ambientLight ?? 0, directLight)),
  };
}

function distanceToRegion(point: Point, region: MaterialRegion): number {
  const polygons = paintedRegionPolygons(region);
  if (polygons.some((polygon) => pointInPolygon(point, polygon))) return 0;
  let nearest = Number.POSITIVE_INFINITY;
  for (const polygon of polygons) for (const vertex of polygon) nearest = Math.min(nearest, Math.hypot(point.x - vertex.x, point.y - vertex.y));
  return nearest;
}

export function fireInfluence(scene: Scene, region: MaterialRegion): number {
  let influence = 0;
  for (const token of scene.tokens) {
    const strength = fireStrength(token);
    if (!strength) continue;
    const distance = distanceToRegion(token.position, region);
    influence = Math.max(influence, strength * Math.max(0, 1 - distance / Math.max(2.5, token.size * 2.2)));
  }
  return Math.min(1, influence);
}

export function waterInfluence(scene: Scene, region: MaterialRegion): number {
  const materialPolygons = paintedRegionPolygons(region, scene.geometry);
  let influence = 0;
  for (const water of scene.waterRegions ?? []) {
    for (const waterPolygon of waterRegionPolygons(water, scene.geometry)) {
      if (materialPolygons.some((polygon) => polygon.some((point) => pointInPolygon(point, waterPolygon)) || waterPolygon.some((point) => pointInPolygon(point, polygon)))) return 1;
      let nearest = Number.POSITIVE_INFINITY;
      for (const materialPolygon of materialPolygons) for (const materialPoint of materialPolygon) for (const waterPoint of waterPolygon) nearest = Math.min(nearest, Math.hypot(materialPoint.x - waterPoint.x, materialPoint.y - waterPoint.y));
      influence = Math.max(influence, Math.max(0, 1 - nearest / 2.5) * (0.45 + Math.max(0, Math.min(1, water.movement ?? 0.55)) * 0.55));
    }
  }
  return Math.min(1, influence);
}

export function materialReaction(material: EnvironmentMaterial, levels: EnvironmentLevels, nearbyFire: number, reactivity = 1, nearbyWater = 0): MaterialReaction {
  const amount = Math.max(0, Math.min(1, reactivity));
  const porous = material === "ground" || material === "vegetation" || material === "wood" || material === "mud";
  const reflective = material === "metal" || material === "ice" || material === "stone";
  const flammable = material === "vegetation" || material === "wood";
  const meltable = material === "ice" || material === "snow";
  const waterLike = material === "ice" || material === "snow" || material === "mud";
  const wetness = Math.min(1, (levels.rain * (porous ? 0.72 : 0.95) + nearbyWater * (porous ? 0.82 : 0.54)) * amount * (1 - nearbyFire * 0.72));
  const snowCover = levels.snow * (meltable ? 0.9 : material === "lava" ? 0 : 0.68) * amount * (1 - nearbyFire * (meltable ? 1 : 0.72));
  const heat = nearbyFire * (flammable ? 1 : material === "metal" || material === "stone" ? 0.54 : 0.32) * amount;
  const steam = nearbyFire * Math.max(levels.rain, nearbyWater, waterLike ? 0.7 : 0) * amount;
  const glow = material === "lava" ? 1 : heat * (flammable ? 0.76 : 0.28);
  const reflection = Math.min(1, (reflective ? 0.32 : 0.08) + wetness * 0.42 + levels.light * (reflective ? 0.34 : 0.12) + levels.lightning * 0.7) * amount;
  const windMotion = levels.wind * (material === "vegetation" ? 1 : material === "snow" ? 0.68 : 0.08) * amount;
  const audio = Math.max(material === "lava" ? 0.68 : 0, material === "vegetation" ? levels.wind : 0, material === "metal" || material === "stone" ? levels.rain * 0.72 : 0, material === "mud" ? levels.rain * 0.48 : 0, heat * 0.65);
  return { wetness, snowCover, windMotion, heat, reflection, steam, glow, audio };
}

export function environmentSoundLevels(scene: Scene): Record<EnvironmentSoundEvent, number> {
  const levels = environmentLevels(scene);
  return { rain: levels.rain, wind: levels.wind, water: levels.water, fire: levels.fire };
}

export function environmentAudioVolumes(scene: Scene): Record<string, number> {
  const result: Record<string, number> = {};
  const eventLevels = environmentSoundLevels(scene);
  for (const [event, binding] of Object.entries(scene.environmentSounds ?? {})) {
    if (!binding?.assetId) continue;
    result[binding.assetId] = Math.max(result[binding.assetId] ?? 0, eventLevels[event as EnvironmentSoundEvent] * Math.max(0, Math.min(1, binding.gain)));
  }
  const levels = environmentLevels(scene);
  for (const region of scene.materialRegions ?? []) {
    if (!region.soundAssetId) continue;
    const reaction = materialReaction(region.material, levels, fireInfluence(scene, region), region.reactivity, waterInfluence(scene, region));
    result[region.soundAssetId] = Math.max(result[region.soundAssetId] ?? 0, reaction.audio * Math.max(0, Math.min(1, region.soundGain ?? 0.7)));
  }
  return result;
}
